import { usePaymentAnalytics } from '../../hooks/usePaymentAnalytics';
import { usePaymentLanguage } from '../../lib/paymentLanguage';
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import type { BankApp, BankPlatform } from '../../data/bankApps';
import type { PaymentMessage } from '../../lib/paymentTranslations';
import { detectBankPlatform, getBankTarget } from '../../lib/bankApps';
import { ANALYTICS_EVENTS } from '../../lib/analytics';
import { Button } from '../ui/button';
import { DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

interface BankOptionsProps {
    paymentId: string;
    transactionId: string;
    banks: readonly BankApp[];
    columns?: 2 | 3;
    qrDescription: PaymentMessage;
    platform?: BankPlatform;
    compact?: boolean;
}

export const BankOptions: React.FC<BankOptionsProps> = ({
    paymentId,
    transactionId,
    banks,
    columns,
    compact = false,
    qrDescription,
    platform = detectBankPlatform(typeof navigator === 'undefined' ? undefined : navigator),
}) => {
    const trackAnalyticsEvent = usePaymentAnalytics();
    const { t } = usePaymentLanguage();
    const titleId = useId();
    const dialogTitleId = useId();
    const dialogDescriptionId = useId();
    const [selectedBankId, setSelectedBankId] = useState<BankApp['id'] | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const availableBanks = banks.flatMap(bank => {
        const url = getBankTarget(bank, platform);
        return url ? [{ bank, url }] : [];
    });
    const selectedBank = availableBanks.find(({ bank }) => bank.id === selectedBankId);
    const dialogOpen = platform === 'desktop' && Boolean(selectedBank);

    useEffect(() => {
        if (!dialogOpen) return;
        const trigger = triggerRef.current;
        closeRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setSelectedBankId(null);
            // Close is the only interactive control in this app-link QR dialog.
            // Keep Tab and Shift+Tab inside it instead of reaching the payment form.
            if (event.key === 'Tab') {
                event.preventDefault();
                closeRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            trigger?.focus();
        };
    }, [dialogOpen]);

    if (availableBanks.length === 0) return null;

    const trackClick = (bank: BankApp) => {
        // This records an attempt only. A click or browser visibility change is
        // neither proof of an app launch nor proof of a payment.
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_BANK_OPEN_CLICKED, {
            payment_id: paymentId,
            transaction_id: transactionId,
            bank_id: bank.id,
            platform,
            interaction: platform === 'desktop' ? 'show_qr' : 'open_app',
        });
    };

    return (
        <section aria-labelledby={titleId} className={compact ? 'bank-options-compact' : 'space-y-3'}>
            <h2 id={titleId} className="text-sm font-semibold text-gray-900 dark:text-slate-100">{t("Откройте приложение банка")}</h2>
            <div className={compact ? 'bank-options-compact-grid' : columns === 3 ? "grid grid-cols-3 gap-2" : columns === 2 ? "grid grid-cols-2 gap-2" : "grid grid-cols-2 gap-2 sm:grid-cols-3"}
                style={compact ? { gridTemplateColumns: `repeat(${availableBanks.length <= 4 ? availableBanks.length : 3}, minmax(0, 1fr))` } : undefined}>
                {availableBanks.map(({ bank, url }) => {
                    const className = compact ? 'bank-options-compact-button' : 'flex min-h-[56px] w-full items-center justify-center rounded-xl px-3 py-2 transition hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 active:translate-y-px';
                    const content = compact ? (
                        <span className="bank-options-compact-logo">
                            <img src={bank.logo} alt="" aria-hidden="true" />
                        </span>
                    ) : (
                        <span className="flex h-9 w-full max-w-[132px] items-center justify-center rounded-lg px-2" style={{ background: bank.logoBackground }}>
                            <img src={bank.logo} alt="" aria-hidden="true" className="h-auto max-h-7 max-w-full object-contain" />
                        </span>
                    );
                    const style = compact ? { background: bank.logoBackground || bank.background } : { background: bank.background, color: bank.color };

                    return platform === 'desktop' ? (
                        <button
                            key={bank.id}
                            type="button"
                            aria-label={t("Открыть {bank}", { bank: bank.name })}
                            title={compact ? bank.name : undefined}
                            className={className}
                            style={style}
                            onClick={event => {
                                triggerRef.current = event.currentTarget;
                                trackClick(bank);
                                setSelectedBankId(bank.id);
                            }}
                        >{content}</button>
                    ) : (
                        <a
                            key={bank.id}
                            href={url}
                            aria-label={t("Открыть {bank}", { bank: bank.name })}
                            title={compact ? bank.name : undefined}
                            referrerPolicy="no-referrer"
                            className={className}
                            style={style}
                            onClick={() => trackClick(bank)}
                        >{content}</a>
                    );
                })}
            </div>
            {dialogOpen && selectedBank && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div aria-hidden="true" className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedBankId(null)} />
                    <div role="dialog" aria-modal="true" aria-labelledby={dialogTitleId} aria-describedby={dialogDescriptionId} className="relative z-50 w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg dark:bg-slate-800">
                        <DialogHeader className="pr-5">
                            <DialogTitle id={dialogTitleId}>{t("Открыть {bank} на телефоне", { bank: selectedBank.bank.name })}</DialogTitle>
                            <DialogDescription id={dialogDescriptionId}>
                                {t(qrDescription)}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 flex justify-center rounded-xl bg-white p-2">
                            <QRCodeSVG value={selectedBank.url} size={240} level="M" includeMargin className="h-auto max-w-full" />
                        </div>
                        <Button ref={closeRef} type="button" variant="outline" className="mt-4 w-full" onClick={() => setSelectedBankId(null)}>{t("Закрыть")}</Button>
                    </div>
                </div>,
                document.body,
            )}
        </section>
    );
};
